import { useSyncExternalStore } from "react";
import { ROSARY_SETS, ROSARY_LANGUAGES, SET_BY_WEEKDAY, buildSteps } from "@/data/rosary";
import { rosaryRoseForStep } from "@/data/rosaryRoses";

const params = new URLSearchParams(window.location.search);
const SESSION_KEY = "theotokos-rosary-session";
const makeId = () => crypto.randomUUID();
export const rosaryWindowId = makeId();
const resolveSession = () => {
  const requested = params.get("session");
  if (requested && /^[a-zA-Z0-9-]{8,64}$/.test(requested)) { try { sessionStorage.setItem(SESSION_KEY, requested); } catch { /* Memory-only session. */ } return requested; }
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = makeId(); sessionStorage.setItem(SESSION_KEY, id); return id;
  } catch { return makeId(); }
};
export const rosarySessionId = resolveSession();
const KEY = `theotokos-rosary-progress:${rosarySessionId}`;
const initial = () => ({ setId: SET_BY_WEEKDAY[new Date().getDay()], lang: "en", stepIx: 0, prayedAnchors: [], playingOwner: null });
const sanitize = (input) => {
  if (!input || !Object.hasOwn(ROSARY_SETS, input.setId) || !ROSARY_LANGUAGES.some((lang) => lang.id === input.lang)) return null;
  const steps = buildSteps(input.setId, input.lang);
  const validAnchors = new Set(steps.filter(rosaryRoseForStep).map((step) => step.anchor));
  return { setId: input.setId, lang: input.lang, stepIx: Number.isInteger(input.stepIx) ? Math.max(0, Math.min(input.stepIx, steps.length - 1)) : 0,
    prayedAnchors: [...new Set(Array.isArray(input.prayedAnchors) ? input.prayedAnchors.filter((anchor) => validAnchors.has(anchor)) : [])],
    playingOwner: typeof input.playingOwner === "string" ? input.playingOwner.slice(0, 64) : null };
};
const load = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY));
    const saved = sanitize(stored?.state);
    if (saved) return { state: { ...saved, playingOwner: null }, revision: stored.revision || 0, origin: stored.origin || "" };
  } catch { /* Continue with the window handoff when storage is blocked. */ }
  try { const handoff = sanitize(JSON.parse(new URLSearchParams(window.location.hash.slice(1)).get("progress"))); if (handoff) return { state: { ...handoff, playingOwner: null }, revision: 0, origin: "" }; } catch { /* Invalid URL state is ignored. */ }
  return { state: initial(), revision: 0, origin: "" };
};
let snapshot = load();
const listeners = new Set();
let channel;
try { channel = new BroadcastChannel(`rosary:${rosarySessionId}`); } catch { /* The storage event is also supported. */ }
const persist = () => { try { localStorage.setItem(KEY, JSON.stringify(snapshot)); } catch { /* In-memory state remains usable. */ } };
const receive = (incoming) => {
  if (!incoming || !Number.isSafeInteger(incoming.revision) || typeof incoming.origin !== "string") return;
  if (incoming.revision < snapshot.revision || (incoming.revision === snapshot.revision && incoming.origin <= snapshot.origin)) return;
  const state = sanitize(incoming.state);
  if (!state) return;
  snapshot = { state, revision: incoming.revision, origin: incoming.origin };
  listeners.forEach((notify) => notify());
};
if (channel) channel.onmessage = ({ data }) => { if (data?.type === "request") channel.postMessage({ type: "snapshot", snapshot }); else receive(data?.snapshot); };
channel?.postMessage({ type: "request" });
window.addEventListener("storage", (event) => { if (event.key === KEY && event.newValue) { try { receive(JSON.parse(event.newValue)); } catch { /* Ignore malformed browser storage. */ } } });
export const updateRosary = (update) => {
  const state = sanitize(typeof update === "function" ? update(snapshot.state) : { ...snapshot.state, ...update });
  if (!state) return;
  snapshot = { state, revision: snapshot.revision + 1, origin: rosaryWindowId };
  persist(); listeners.forEach((notify) => notify()); channel?.postMessage({ type: "snapshot", snapshot });
};
window.addEventListener("pagehide", () => { if (snapshot.state.playingOwner === rosaryWindowId) updateRosary({ playingOwner: null }); });
const subscribe = (notify) => { listeners.add(notify); return () => listeners.delete(notify); };
export const useRosarySession = () => useSyncExternalStore(subscribe, () => snapshot.state);
export const openRosaryWindow = () => {
  updateRosary({ playingOwner: null });
  const url = new URL("/rosary/popout", window.location.origin);
  url.searchParams.set("session", rosarySessionId);
  // No intention or personal data: only mystery, language, position and offered beads.
  // The fragment stays in the browser; unlike a query string it is not sent to the server.
  url.hash = new URLSearchParams({ progress: JSON.stringify(snapshot.state) }).toString();
  try {
    const child = window.open(url.toString(), `rosary-${rosarySessionId}`, "popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes");
    if (child) { child.focus(); return true; }
  } catch { /* Offer the in-page view if the browser rejects a popup. */ }
  return false;
};